// /opt/isp_erp/frontend/src/pages/admin/clientes/CorteMasivo.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Paper, Typography, Grid, TextField, MenuItem, FormControlLabel, Checkbox, FormGroup, Button, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions,
} from "@mui/material";
import axios from "../../../api/axios";
import { mensajeError } from "../../../utils/mensajesError";

const ESTADOS = [
  { valor: "ACTIVO", etiqueta: "Activos" },
  { valor: "SUSPENDIDO", etiqueta: "Suspendidos" },
  { valor: "PENDIENTE_INSTALACION", etiqueta: "Pendientes de instalación" },
  { valor: "RETIRADO", etiqueta: "Retirados" },
];
const DIAS = Array.from({ length: 31 }, (_, i) => i + 1);

const entero = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));

const describir = (c) => {
  const partes = [];
  if (c.corte_automatico !== undefined) partes.push(`corte automático: ${c.corte_automatico ? "activar" : "desactivar"}`);
  if (c.dia_corte !== undefined) partes.push(`día de corte: ${c.dia_corte}`);
  if (c.hora_corte !== undefined) partes.push(`hora: ${c.hora_corte}`);
  if (c.minuto_corte !== undefined) partes.push(`minuto: ${c.minuto_corte}`);
  if (c.tolerancia_dias !== undefined) partes.push(`tolerancia: ${c.tolerancia_dias} día(s)`);
  return partes.join(" · ");
};

export default function CorteMasivo() {
  const [opciones, setOpciones] = useState({ sucursales: [], puede_editar: true });
  const [estados, setEstados] = useState(["ACTIVO"]);
  const [sucursalId, setSucursalId] = useState("");
  const [corteActual, setCorteActual] = useState("");
  const [diaActual, setDiaActual] = useState("");
  const [buscar, setBuscar] = useState("");
  const [cambioAuto, setCambioAuto] = useState("");
  const [nuevoDia, setNuevoDia] = useState("");
  const [nuevaHora, setNuevaHora] = useState("");
  const [nuevoMinuto, setNuevoMinuto] = useState("");
  const [nuevaTolerancia, setNuevaTolerancia] = useState("");
  const [vista, setVista] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    axios.get("/customers/corte/opciones")
      .then((r) => setOpciones(r.data))
      .catch(() => setOpciones({ sucursales: [], puede_editar: true }));
  }, []);

  const filtros = useMemo(() => ({
    estados,
    sucursal_id: sucursalId === "" ? null : Number(sucursalId),
    corte_automatico: corteActual === "" ? null : corteActual === "SI",
    sin_dia_corte: diaActual === "SIN" ? true : null,
    dia_corte: diaActual !== "" && diaActual !== "SIN" ? Number(diaActual) : null,
    buscar: buscar.trim() || null,
  }), [estados, sucursalId, corteActual, diaActual, buscar]);

  const cambios = useMemo(() => {
    const c = {};
    if (cambioAuto) c.corte_automatico = cambioAuto === "ACTIVAR";
    if (entero(nuevoDia) !== null) c.dia_corte = entero(nuevoDia);
    if (entero(nuevaHora) !== null) c.hora_corte = entero(nuevaHora);
    if (entero(nuevoMinuto) !== null) c.minuto_corte = entero(nuevoMinuto);
    if (entero(nuevaTolerancia) !== null) c.tolerancia_dias = entero(nuevaTolerancia);
    return c;
  }, [cambioAuto, nuevoDia, nuevaHora, nuevoMinuto, nuevaTolerancia]);

  // Cualquier cambio en filtros o cambios invalida la vista previa
  useEffect(() => {
    setVista(null);
    setResultado(null);
  }, [filtros, cambios]);

  const alternarEstado = (valor) => {
    setEstados((prev) => (prev.includes(valor) ? prev.filter((e) => e !== valor) : [...prev, valor]));
  };

  const verVistaPrevia = async () => {
    setCargando(true);
    setError("");
    setResultado(null);
    try {
      const r = await axios.post("/customers/corte/masivo/vista-previa", { filtros, cambios });
      setVista(r.data);
    } catch (e) {
      setVista(null);
      setError(mensajeError(e, "No se pudo calcular la vista previa"));
    } finally {
      setCargando(false);
    }
  };

  const aplicar = async () => {
    setAplicando(true);
    setError("");
    try {
      const r = await axios.post("/customers/corte/masivo/aplicar", { filtros, cambios, confirmar_total: vista.total });
      setResultado(r.data);
      setVista(null);
    } catch (e) {
      setError(mensajeError(e, "No se pudo aplicar el cambio"));
    } finally {
      setAplicando(false);
      setConfirmando(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h5" gutterBottom>Corte masivo</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Cambia el corte de muchos clientes a la vez. Primero verás a cuántos afecta; no se guarda nada hasta que confirmes.
      </Typography>
      {!opciones.puede_editar && (
        <Alert severity="warning" sx={{ mb: 2 }}>Solo admin, supervisor o finanzas pueden aplicar cambios de corte.</Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>1. ¿A quiénes?</Typography>
        <FormGroup row sx={{ mb: 1 }}>
          {ESTADOS.map((e) => (
            <FormControlLabel
              key={e.valor}
              control={<Checkbox size="small" checked={estados.includes(e.valor)} onChange={() => alternarEstado(e.valor)} />}
              label={e.etiqueta}
            />
          ))}
        </FormGroup>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField select size="small" fullWidth label="Sucursal" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {opciones.sucursales.map((s) => (<MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select size="small" fullWidth label="Corte automático actual" value={corteActual} onChange={(e) => setCorteActual(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="SI">Activado</MenuItem>
              <MenuItem value="NO">Desactivado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select size="small" fullWidth label="Día de corte actual" value={diaActual} onChange={(e) => setDiaActual(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="SIN">Sin día de corte</MenuItem>
              {DIAS.map((d) => (<MenuItem key={d} value={String(d)}>{d}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField size="small" fullWidth label="Nombre o cédula" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>2. ¿Qué cambiar? (deja en blanco lo que no quieras tocar)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField select size="small" fullWidth label="Corte automático" value={cambioAuto} onChange={(e) => setCambioAuto(e.target.value)}>
              <MenuItem value="">No cambiar</MenuItem>
              <MenuItem value="ACTIVAR">Activar</MenuItem>
              <MenuItem value="DESACTIVAR">Desactivar</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField size="small" fullWidth type="number" label="Día de corte" value={nuevoDia} onChange={(e) => setNuevoDia(e.target.value)} inputProps={{ min: 1, max: 31 }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField size="small" fullWidth type="number" label="Hora" value={nuevaHora} onChange={(e) => setNuevaHora(e.target.value)} inputProps={{ min: 0, max: 23 }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField size="small" fullWidth type="number" label="Minuto" value={nuevoMinuto} onChange={(e) => setNuevoMinuto(e.target.value)} inputProps={{ min: 0, max: 59 }} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField size="small" fullWidth type="number" label="Días de tolerancia" value={nuevaTolerancia} onChange={(e) => setNuevaTolerancia(e.target.value)} inputProps={{ min: 0, max: 60 }} />
          </Grid>
        </Grid>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={verVistaPrevia} disabled={cargando || estados.length === 0}>
          {cargando ? "Calculando..." : "Ver a quién afecta"}
        </Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {resultado && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {[resultado.mensaje, ...(resultado.advertencias || [])].join(" ")}
        </Alert>
      )}

      {vista && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>3. Vista previa</Typography>
          <Typography sx={{ mb: 1 }}>
            Afecta a <strong>{vista.total}</strong> cliente(s)
            {vista.sin_dia_corte ? ` (${vista.sin_dia_corte} sin día de corte)` : ""}.
          </Typography>
          {(vista.problemas || []).map((p) => (<Alert key={p} severity="error" sx={{ mb: 1 }}>{p}</Alert>))}
          {(vista.advertencias || []).map((a) => (<Alert key={a} severity="warning" sx={{ mb: 1 }}>{a}</Alert>))}
          {vista.muestra && vista.muestra.length > 0 && (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente (primeros {vista.muestra.length})</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Día actual</TableCell>
                  <TableCell>Corte automático</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vista.muestra.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.cliente}</TableCell>
                    <TableCell>{m.estado}</TableCell>
                    <TableCell>{m.dia_corte || "—"}</TableCell>
                    <TableCell>{m.corte_automatico ? "Sí" : "No"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Button variant="contained" color="warning" sx={{ mt: 2 }} disabled={!vista.puede_aplicar || aplicando || !opciones.puede_editar} onClick={() => setConfirmando(true)}>
            Aplicar a {vista.total} cliente(s)
          </Button>
        </Paper>
      )}

      <Dialog open={confirmando} onClose={() => (aplicando ? null : setConfirmando(false))}>
        <DialogTitle>Confirmar cambio masivo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se cambiará el corte de {vista ? vista.total : 0} cliente(s): {describir(cambios)}. Queda registrado en la bitácora.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmando(false)} disabled={aplicando}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={aplicar} disabled={aplicando}>
            {aplicando ? "Aplicando..." : "Sí, aplicar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
